// src/hooks/useWordbookGroup.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { wordbookGroupApi } from '../api/wordbookGroupApi';
import { WorkbookGroupFilterParams } from '../models/wordbookGroup/wordbookGroupFilterParams';
import { CreateWordbookGroupRequest } from '../models/wordbookGroup/createWordbookGroupRequest';
import { UpdateWordbookGroupRequest } from '../models/wordbookGroup/updateWordbookGroupRequest';
import { Visibility } from '../types/wordbookGroup';

// 목록 조회
export const useWordbookGroupList = (params: WorkbookGroupFilterParams) => {
    return useQuery({
        queryKey: ['wordbookGroups', params],
        queryFn: () => wordbookGroupApi.getList(params),
    });
};

// 단건 조회
export const useWordbookGroup = (id: number) => {
    return useQuery({
        queryKey: ['wordbookGroup', id],
        queryFn: () => wordbookGroupApi.getOne(id),
        enabled: !!id,
    });
};

// 생성
export const useCreateWordbookGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateWordbookGroupRequest) => wordbookGroupApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wordbookGroups'] });
        },
    });
};

// 수정
export const useUpdateWordbookGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateWordbookGroupRequest }) =>
            wordbookGroupApi.update(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['wordbookGroup', variables.id] });
            queryClient.invalidateQueries({ queryKey: ['wordbookGroups'] });
        },
    });
};

// 삭제
export const useDeleteWordbookGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => wordbookGroupApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wordbookGroups'] });
        },
    });
};

// 영구 삭제
export const usePermanentDeleteWordbookGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => wordbookGroupApi.permanentDelete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wordbookGroups'] });
        },
    });
};

// 복원
export const useRestoreWordbookGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => wordbookGroupApi.restore(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wordbookGroups'] });
        },
    });
};

// 노출 토글
export const useToggleWordbookGroupVisibility = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => wordbookGroupApi.toggleVisibility(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ['wordbookGroup', id] });
            queryClient.invalidateQueries({ queryKey: ['wordbookGroups'] });
        },
    });
};

// 통계 조회
export const useWordbookGroupStats = (id: number) => {
    return useQuery({
        queryKey: ['wordbookGroupStats', id],
        queryFn: () => wordbookGroupApi.getStats(id),
        enabled: !!id,
    });
};

// 일괄 삭제
export const useBulkDeleteWordbookGroups = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (groupIds: number[]) => wordbookGroupApi.bulkDelete(groupIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wordbookGroups'] });
        },
    });
};

// 일괄 노출 설정
export const useBulkUpdateGroupVisibility = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ groupIds, visibility }: { groupIds: number[]; visibility: Visibility }) =>
            wordbookGroupApi.bulkUpdateVisibility(groupIds, visibility),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wordbookGroups'] });
        },
    });
};

// 전체 통계
export const useWordbookGroupOverviewStats = () => {
    return useQuery({
        queryKey: ['wordbookGroupOverviewStats'],
        queryFn: () => wordbookGroupApi.getOverviewStats(),
    });
};

// 언어별 통계
export const useWordbookGroupLanguageStats = () => {
    return useQuery({
        queryKey: ['wordbookGroupLanguageStats'],
        queryFn: () => wordbookGroupApi.getLanguageStats(),
    });
};
